import { useState, useEffect, useContext, useCallback } from "react";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaSearch,
  FaCheck,
  FaTimes,
  FaSync,
} from "react-icons/fa";
import AuthContext from "../../contexts/AuthContext";
import {
  getInventoryItems,
  createInventoryItem,
  updateInventoryItem,
  deleteInventoryItem,
  getSuppliers,
} from "../../utils/inventoryApi";
import {
  getOpeningStock,
  createOpeningStock,
  updateOpeningStock,
} from "../../utils/inventoryApi";

const uomOptions = ["kg", "pcs", "liters", "g", "ml", "box", "pack"];

export default function InventoryItems() {
  const { restaurantId } = useContext(AuthContext);
  const [items, setItems] = useState([]);
  const [openingStockItems, setOpeningStockItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingRow, setEditingRow] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterLocation, setFilterLocation] = useState("all");
  const [locations] = useState(["Main Store"]); // Only Main Store
  const [selectedSupplier, setSelectedSupplier] = useState("");
  const [purchaseDate, setPurchaseDate] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showAllItems, setShowAllItems] = useState(false);

  // Form state for table editing
  const [form, setForm] = useState({
    prodName: "",
    prodId: "",
    uom: "",
    stockQty: "",
    stockRate: "",
  });

  useEffect(() => {
    if (restaurantId) {
      fetchItems();
      fetchOpeningStock();
      fetchSuppliers();
    }
  }, [restaurantId, fetchItems, fetchOpeningStock, fetchSuppliers]);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await getInventoryItems(restaurantId);
      setItems(data || []);
    } catch (err) {
      setError("Failed to fetch items");
    }
    setLoading(false);
  }, [restaurantId]);

  const fetchOpeningStock = useCallback(async () => {
    try {
      const data = await getOpeningStock(restaurantId, "main");
      setOpeningStockItems(data || []);
    } catch (err) {
      console.error("Failed to fetch opening stock:", err);
    }
  }, [restaurantId]);

  const fetchSuppliers = useCallback(async () => {
    try {
      const data = await getSuppliers(restaurantId);
      setSuppliers(data || []);
      // Set first supplier as default if available
      if (data && data.length > 0 && !selectedSupplier) {
        setSelectedSupplier(data[0].name);
      }
    } catch (err) {
      console.error("Failed to fetch suppliers:", err);
    }
  }, [restaurantId, selectedSupplier]);

  function handleAddRow() {
    setEditingRow("new");
    setForm({
      prodName: "",
      prodId: getNextProdId().toString(),
      uom: "",
      stockQty: "",
      stockRate: "",
    });
    setError("");
  }

  function handleEditRow(item) {
    setEditingRow(item.id);
    setForm({
      prodName: item.name || "",
      prodId: item.prodId || "",
      uom: item.unit || "",
      stockQty: item.currentStock?.toString() || "",
      stockRate: item.costPrice?.toString() || "",
    });
    setError("");
  }

  function handleCancelEdit() {
    setEditingRow(null);
    setForm({ prodName: "", prodId: "", uom: "", stockQty: "", stockRate: "" });
    setError("");
  }

  function getNextProdId() {
    if (openingStockItems.length === 0) return 1001;
    const maxId = Math.max(
      ...openingStockItems.map((r) => parseInt(r.prodId, 10) || 1000)
    );
    return maxId + 1;
  }

  function handleProductNameChange(e) {
    const prodName = e.target.value;
    setForm((prev) => ({ ...prev, prodName }));

    // Check if product exists in opening stock
    const existingProduct = openingStockItems.find(
      (item) => item.prodName.toLowerCase() === prodName.toLowerCase()
    );

    if (existingProduct) {
      // Auto-fill existing product data
      setForm((prev) => ({
        ...prev,
        prodName: existingProduct.prodName,
        prodId: existingProduct.prodId,
        uom: existingProduct.uom,
        stockRate: existingProduct.stockRate?.toString() || "",
      }));
    } else {
      // Clear fields for new product and generate new ID
      setForm((prev) => ({
        ...prev,
        prodId: getNextProdId().toString(),
        uom: "",
        stockRate: "",
      }));
    }
  }

  async function handleSaveRow() {
    // Enhanced validation
    if (!form.prodName || !form.prodName.trim()) {
      setError("Product name is required");
      return;
    }
    if (!form.uom) {
      setError("UOM is required");
      return;
    }
    if (!form.stockQty || Number(form.stockQty) <= 0) {
      setError("Stock quantity must be greater than 0");
      return;
    }
    if (!form.stockRate || Number(form.stockRate) <= 0) {
      setError("Stock rate must be greater than 0");
      return;
    }
    if (!selectedSupplier) {
      setError("Please select a supplier");
      return;
    }
    if (!purchaseDate) {
      setError("Please select a purchase date");
      return;
    }

    setError("");

    const stockValue = Number(form.stockQty) * Number(form.stockRate);
    const isNewProduct = !openingStockItems.find(
      (item) => item.prodName.toLowerCase() === form.prodName.toLowerCase()
    );

    try {
      if (editingRow === "new") {
        // Check if product exists in opening stock
        if (isNewProduct) {
          // Create new product in opening stock first
          const openingStockData = {
            prodId: form.prodId,
            prodName: form.prodName,
            uom: form.uom,
            stockQty: Number(form.stockQty),
            stockRate: Number(form.stockRate),
            stockValue,
            storeId: "main",
          };

          console.log("Creating opening stock item:", openingStockData);
          await createOpeningStock(restaurantId, openingStockData);
          await fetchOpeningStock(); // Refresh opening stock
        }

        // Create inventory item
        const inventoryData = {
          name: form.prodName,
          prodId: form.prodId,
          unit: form.uom,
          currentStock: Number(form.stockQty),
          costPrice: Number(form.stockRate),
          supplier: selectedSupplier,
          purchaseDate: purchaseDate,
          location: "Main Store",
          minStock: 0,
          maxStock: Number(form.stockQty) * 2,
          barcode: "",
          description: `Purchased from ${selectedSupplier} on ${purchaseDate}`,
        };

        console.log("Creating inventory item:", inventoryData);
        await createInventoryItem(restaurantId, inventoryData);
        await fetchItems();
      } else {
        // Update existing item
        const inventoryData = {
          name: form.prodName,
          prodId: form.prodId,
          unit: form.uom,
          currentStock: Number(form.stockQty),
          costPrice: Number(form.stockRate),
          supplier: selectedSupplier,
          purchaseDate: purchaseDate,
        };

        console.log("Updating inventory item:", editingRow, inventoryData);
        await updateInventoryItem(restaurantId, editingRow, inventoryData);

        // Also update opening stock if rate changed for existing product
        if (!isNewProduct) {
          const openingStockItem = openingStockItems.find(
            (item) =>
              item.prodName.toLowerCase() === form.prodName.toLowerCase()
          );
          if (openingStockItem) {
            console.log("Updating opening stock item:", openingStockItem.id);
            await updateOpeningStock(restaurantId, openingStockItem.id, {
              stockRate: Number(form.stockRate),
              stockValue: stockValue,
            });
            await fetchOpeningStock(); // Refresh opening stock
          }
        }

        await fetchItems();
      }

      setEditingRow(null);
      setForm({
        prodName: "",
        prodId: "",
        uom: "",
        stockQty: "",
        stockRate: "",
      });
      setSuccess("Item saved successfully!");
      setTimeout(() => setSuccess(""), 3000);
    } catch (error) {
      console.error("Error saving item:", error);
      // Handle specific validation errors from backend
      if (error.response && error.response.data && error.response.data.error) {
        setError(error.response.data.error);
      } else {
        setError("Failed to save item. Please try again.");
      }
    }
  }

  async function handleDeleteRow(id) {
    if (!window.confirm("Delete this item?")) return;

    try {
      await deleteInventoryItem(restaurantId, id);
      await fetchItems();
      setSuccess("Item deleted successfully!");
      setTimeout(() => setSuccess(""), 3000);
    } catch (error) {
      console.error("Error deleting item:", error);
      setError("Failed to delete item. Please try again.");
    }
  }

  function handleInputChange(e) {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (error) setError("");
    if (success) setSuccess("");
  }

  // Filter items based on search and filters
  const filteredItems = items.filter((item) => {
    const matchesSearch =
      item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesLocation =
      filterLocation === "all" || item.location === filterLocation;
    return matchesSearch && matchesLocation;
  });

  // Get items to display (first 10 or all based on showAllItems)
  const itemsToDisplay = showAllItems
    ? filteredItems
    : filteredItems.slice(0, 10);
  const hasMoreItems = filteredItems.length > 10;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-800 dark:text-white">
            Inventory Items
          </h1>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300 mt-1 sm:mt-2">
            Add inventory items with supplier information
          </p>
        </div>
        <div className="flex gap-2 sm:gap-3">
          <button
            className="rounded-full bg-blue-100 hover:bg-blue-200 p-2"
            onClick={handleAddRow}
          >
            <FaPlus className="text-blue-600 text-lg sm:text-xl" />
          </button>
          <button
            className="rounded-full bg-amber-100 hover:bg-amber-200 p-2"
            onClick={fetchItems}
          >
            <FaSync className="text-amber-700 text-lg sm:text-xl" />
          </button>
        </div>
      </div>

      {/* Filters and Supplier Selection */}
      <div className="bg-white dark:bg-slate-800 rounded-lg p-3 sm:p-4 lg:p-6 shadow-sm border border-gray-200 dark:border-slate-700">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
              Search
            </label>
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm" />
              <input
                type="text"
                placeholder="Search items..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-8 sm:pl-10 pr-3 sm:pr-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-rose-500 dark:bg-slate-700 dark:text-white text-sm"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
              Location
            </label>
            <select
              value={filterLocation}
              onChange={(e) => setFilterLocation(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500 dark:bg-slate-700 dark:text-white text-sm"
            >
              <option value="all">All Locations</option>
              {locations.map((location) => (
                <option key={location} value={location}>
                  {location}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
              Supplier
            </label>
            <select
              value={selectedSupplier}
              onChange={(e) => setSelectedSupplier(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500 dark:bg-slate-700 dark:text-white text-sm"
            >
              <option value="">Select Supplier</option>
              {suppliers.map((supplier) => (
                <option key={supplier.id} value={supplier.name}>
                  {supplier.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
              Purchase Date
            </label>
            <input
              type="date"
              value={purchaseDate}
              onChange={(e) => setPurchaseDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500 dark:bg-slate-700 dark:text-white text-sm"
            />
          </div>
        </div>
      </div>

      {/* Summary Info */}
      <div className="mb-4 p-3 sm:p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
          <div className="text-center">
            <div className="text-lg sm:text-xl lg:text-2xl font-bold text-blue-600 dark:text-blue-400">
              {filteredItems.length}
            </div>
            <div className="text-xs sm:text-sm text-blue-600 dark:text-blue-400">
              Total Items
            </div>
          </div>
          <div className="text-center">
            <div className="text-lg sm:text-xl lg:text-2xl font-bold text-green-600 dark:text-green-400">
              ₹
              {filteredItems
                .reduce(
                  (sum, item) =>
                    sum + (item.currentStock * item.costPrice || 0),
                  0
                )
                .toLocaleString()}
            </div>
            <div className="text-xs sm:text-sm text-green-600 dark:text-green-400">
              Total Value
            </div>
          </div>
          <div className="text-center">
            <div className="text-base sm:text-lg lg:text-2xl font-bold text-purple-600 dark:text-purple-400 truncate">
              {selectedSupplier}
            </div>
            <div className="text-xs sm:text-sm text-purple-600 dark:text-purple-400">
              Selected Supplier
            </div>
          </div>
        </div>
      </div>

      {/* Items Table */}
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-3 sm:p-6">
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-slate-700">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-slate-700">
            <thead className="bg-gray-200 dark:bg-slate-700">
              <tr>
                <th className="px-2 sm:px-4 py-2 text-left text-xs font-semibold text-gray-700 dark:text-gray-200">
                  SNo
                </th>
                <th className="px-2 sm:px-4 py-2 text-left text-xs font-semibold text-gray-700 dark:text-gray-200">
                  Prod Id
                </th>
                <th className="px-2 sm:px-4 py-2 text-left text-xs font-semibold text-gray-700 dark:text-gray-200">
                  Prod Name
                </th>
                <th className="px-2 sm:px-4 py-2 text-left text-xs font-semibold text-gray-700 dark:text-gray-200">
                  UOM
                </th>
                <th className="px-2 sm:px-4 py-2 text-left text-xs font-semibold text-gray-700 dark:text-gray-200">
                  Stock Qty
                </th>
                <th className="px-2 sm:px-4 py-2 text-left text-xs font-semibold text-gray-700 dark:text-gray-200">
                  Stock Rate
                </th>
                <th className="px-2 sm:px-4 py-2 text-left text-xs font-semibold text-gray-700 dark:text-gray-200">
                  Stock Value
                </th>
                <th className="px-2 sm:px-4 py-2 text-left text-xs font-semibold text-gray-700 dark:text-gray-200">
                  Supplier
                </th>
                <th className="px-2 sm:px-4 py-2 text-left text-xs font-semibold text-gray-700 dark:text-gray-200">
                  Purchase Date
                </th>
                <th className="px-2 sm:px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-slate-800 divide-y divide-gray-200 dark:divide-slate-700">
              {filteredItems.length === 0 && editingRow !== "new" ? (
                <tr>
                  <td
                    colSpan={10}
                    className="text-center py-6 sm:py-8 text-gray-400 dark:text-gray-500 text-sm sm:text-base"
                  >
                    No inventory items found. Click the + button to add items.
                  </td>
                </tr>
              ) : (
                <>
                  {editingRow === "new" && (
                    <tr>
                      <td className="px-2 sm:px-4 py-2 text-xs sm:text-sm">
                        {filteredItems.length + 1}
                      </td>
                      <td className="px-2 sm:px-4 py-2 text-xs sm:text-sm">
                        {form.prodId}
                      </td>
                      <td className="px-2 sm:px-4 py-2">
                        <input
                          name="prodName"
                          value={form.prodName}
                          onChange={handleProductNameChange}
                          className="border rounded px-1 sm:px-2 py-1 w-20 sm:w-24 lg:w-32 text-xs sm:text-sm"
                          placeholder="Product name"
                        />
                      </td>
                      <td className="px-2 sm:px-4 py-2">
                        <select
                          name="uom"
                          value={form.uom}
                          onChange={handleInputChange}
                          className="border rounded px-1 sm:px-2 py-1 text-xs sm:text-sm"
                        >
                          <option value="">Select</option>
                          {uomOptions.map((u) => (
                            <option key={u} value={u}>
                              {u}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-2 sm:px-4 py-2">
                        <input
                          name="stockQty"
                          type="number"
                          value={form.stockQty}
                          onChange={handleInputChange}
                          className="border rounded px-1 sm:px-2 py-1 w-12 sm:w-16 lg:w-20 text-xs sm:text-sm"
                          placeholder="Qty"
                        />
                      </td>
                      <td className="px-2 sm:px-4 py-2">
                        <input
                          name="stockRate"
                          type="number"
                          value={form.stockRate}
                          onChange={handleInputChange}
                          className="border rounded px-1 sm:px-2 py-1 w-12 sm:w-16 lg:w-20 text-xs sm:text-sm"
                          placeholder="Rate"
                        />
                      </td>
                      <td className="px-2 sm:px-4 py-2 text-xs sm:text-sm">
                        {form.stockQty && form.stockRate
                          ? Number(form.stockQty) * Number(form.stockRate)
                          : ""}
                      </td>
                      <td className="px-2 sm:px-4 py-2 text-xs sm:text-sm">
                        {selectedSupplier}
                      </td>
                      <td className="px-2 sm:px-4 py-2 text-xs sm:text-sm">
                        {purchaseDate}
                      </td>
                      <td className="px-2 sm:px-4 py-2">
                        <button
                          className="text-green-600 mr-1 sm:mr-2 p-1"
                          onClick={handleSaveRow}
                        >
                          <FaCheck className="text-sm sm:text-base" />
                        </button>
                        <button
                          className="text-gray-400 p-1"
                          onClick={handleCancelEdit}
                        >
                          <FaTimes className="text-sm sm:text-base" />
                        </button>
                      </td>
                    </tr>
                  )}
                  {itemsToDisplay.map((item, idx) =>
                    editingRow === item.id ? (
                      <tr key={item.id}>
                        <td className="px-2 sm:px-4 py-2 text-xs sm:text-sm">
                          {idx + 1}
                        </td>
                        <td className="px-2 sm:px-4 py-2 text-xs sm:text-sm">
                          {form.prodId}
                        </td>
                        <td className="px-2 sm:px-4 py-2">
                          <input
                            name="prodName"
                            value={form.prodName}
                            onChange={handleProductNameChange}
                            className="border rounded px-1 sm:px-2 py-1 w-20 sm:w-24 lg:w-32 text-xs sm:text-sm"
                          />
                        </td>
                        <td className="px-2 sm:px-4 py-2">
                          <select
                            name="uom"
                            value={form.uom}
                            onChange={handleInputChange}
                            className="border rounded px-1 sm:px-2 py-1 text-xs sm:text-sm"
                          >
                            <option value="">Select</option>
                            {uomOptions.map((u) => (
                              <option key={u} value={u}>
                                {u}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="px-2 sm:px-4 py-2">
                          <input
                            name="stockQty"
                            type="number"
                            value={form.stockQty}
                            onChange={handleInputChange}
                            className="border rounded px-1 sm:px-2 py-1 w-12 sm:w-16 lg:w-20 text-xs sm:text-sm"
                          />
                        </td>
                        <td className="px-2 sm:px-4 py-2">
                          <input
                            name="stockRate"
                            type="number"
                            value={form.stockRate}
                            onChange={handleInputChange}
                            className="border rounded px-1 sm:px-2 py-1 w-12 sm:w-16 lg:w-20 text-xs sm:text-sm"
                          />
                        </td>
                        <td className="px-2 sm:px-4 py-2 text-xs sm:text-sm">
                          {form.stockQty && form.stockRate
                            ? Number(form.stockQty) * Number(form.stockRate)
                            : ""}
                        </td>
                        <td className="px-2 sm:px-4 py-2 text-xs sm:text-sm">
                          {selectedSupplier}
                        </td>
                        <td className="px-2 sm:px-4 py-2 text-xs sm:text-sm">
                          {purchaseDate}
                        </td>
                        <td className="px-2 sm:px-4 py-2">
                          <button
                            className="text-green-600 mr-1 sm:mr-2 p-1"
                            onClick={handleSaveRow}
                          >
                            <FaCheck className="text-sm sm:text-base" />
                          </button>
                          <button
                            className="text-gray-400 p-1"
                            onClick={handleCancelEdit}
                          >
                            <FaTimes className="text-sm sm:text-base" />
                          </button>
                        </td>
                      </tr>
                    ) : (
                      <tr key={item.id}>
                        <td className="px-2 sm:px-4 py-2 text-xs sm:text-sm">
                          {idx + 1}
                        </td>
                        <td className="px-2 sm:px-4 py-2 text-xs sm:text-sm">
                          {item.prodId || "N/A"}
                        </td>
                        <td className="px-2 sm:px-4 py-2 text-xs sm:text-sm">
                          {item.name}
                        </td>
                        <td className="px-2 sm:px-4 py-2 text-xs sm:text-sm">
                          {item.unit}
                        </td>
                        <td className="px-2 sm:px-4 py-2 text-xs sm:text-sm">
                          {item.currentStock}
                        </td>
                        <td className="px-2 sm:px-4 py-2 text-xs sm:text-sm">
                          {item.costPrice}
                        </td>
                        <td className="px-2 sm:px-4 py-2 text-xs sm:text-sm">
                          {item.currentStock * item.costPrice}
                        </td>
                        <td className="px-2 sm:px-4 py-2 text-xs sm:text-sm">
                          {item.supplier}
                        </td>
                        <td className="px-2 sm:px-4 py-2 text-xs sm:text-sm">
                          {item.purchaseDate}
                        </td>
                        <td className="px-2 sm:px-4 py-2">
                          <button
                            className="text-blue-600 mr-1 sm:mr-2 p-1"
                            onClick={() => handleEditRow(item)}
                          >
                            <FaEdit className="text-sm sm:text-base" />
                          </button>
                          <button
                            className="text-red-600 p-1"
                            onClick={() => handleDeleteRow(item.id)}
                          >
                            <FaTrash className="text-sm sm:text-base" />
                          </button>
                        </td>
                      </tr>
                    )
                  )}
                  {hasMoreItems && (
                    <tr>
                      <td colSpan={10} className="text-center py-2">
                        <button
                          className="text-blue-600 hover:underline text-sm sm:text-base"
                          onClick={() => setShowAllItems(!showAllItems)}
                        >
                          {showAllItems ? "Read Less" : "Read More"}
                        </button>
                      </td>
                    </tr>
                  )}
                </>
              )}
              {error && (
                <tr>
                  <td
                    colSpan={10}
                    className="text-center text-red-500 py-2 text-sm sm:text-base"
                  >
                    {error}
                  </td>
                </tr>
              )}
              {success && (
                <tr>
                  <td
                    colSpan={10}
                    className="text-center text-green-500 py-2 text-sm sm:text-base"
                  >
                    {success}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
